import React, { useState, useEffect } from 'react';
import { UserCheck, Building2, Briefcase, MapPin, RefreshCw, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useAuth, TestPersona } from '@/providers/AuthProvider';
import { authService } from '@/services/authService';
import { waitForAuthReady } from '@/services/apiClient';

interface PersonaSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PersonaSelectorModal: React.FC<PersonaSelectorModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { switchToTestPersona, clearTestPersona, user, isDevelopmentMode } = useAuth();
  const [personas, setPersonas] = useState<TestPersona[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterCampus, setFilterCampus] = useState<string>('all');
  // Set when the backend refuses this account every test persona.
  const [notEnabled, setNotEnabled] = useState(false);
  
  // Get the ORIGINAL user info (not the test persona)
  // We store it in localStorage when a real user first logs in
  const getOriginalUserEmail = (): string | null => {
    // First check localStorage for saved original email
    const savedOriginalEmail = localStorage.getItem('original_user_email');
    if (savedOriginalEmail) {
      return savedOriginalEmail;
    }
    
    // If current user email doesn't look like a test email, it's the original
    if (user?.email && !user.email.includes('@eduvos-uat.com') && !user.email.startsWith('test.')) {
      // Save it for future use
      localStorage.setItem('original_user_email', user.email);
      localStorage.setItem('original_user_firstName', user.firstName || '');
      localStorage.setItem('original_user_lastName', user.lastName || '');
      return user.email;
    }
    
    return null;
  };
  
  const getOriginalUserName = () => {
    const firstName = localStorage.getItem('original_user_firstName') || user?.firstName || '';
    const lastName = localStorage.getItem('original_user_lastName') || user?.lastName || '';
    return { firstName, lastName };
  };

  // Load personas when modal opens
  useEffect(() => {
    if (open && isDevelopmentMode) {
      loadPersonas();
    }
  }, [open, isDevelopmentMode]);

  const loadPersonas = async () => {
    setLoading(true);
    try {
      // Falls back to a relative URL, not localhost: the dev compose leaves
      // VITE_API_BASE_URL empty so calls go through the Vite proxy, and an
      // absolute localhost:8000 resolves to the VIEWER's machine — which breaks
      // the moment the dev server is reached from anywhere but this box. UAT
      // sets the var explicitly, so it is unaffected.
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      const allPersonas: TestPersona[] = [];
      
      // Get the ORIGINAL user email (not test persona email)
      const originalEmail = getOriginalUserEmail();
      // The real account's token and never the persona headers, so the backend judges who
      // is actually signed in. It answers 403 for personas this account may not use.
      // Hold until a usable token exists, as every API call does (authReadiness.ts).
      await waitForAuthReady();
      const token = authService.getToken();
      const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      let refused = false;
      
      // First, try to load UAT personas for the original user
      if (originalEmail) {
        try {
          if (import.meta.env.DEV) console.log('🎯 Loading UAT personas for:', originalEmail);
          const uatResponse = await fetch(
            `${apiBaseUrl}/api/v1/integration/uat/participants/${encodeURIComponent(originalEmail)}/personas`,
            { headers: authHeaders }
          );
          refused = refused || uatResponse.status === 403;
          if (uatResponse.ok) {
            const uatData = await uatResponse.json();
            const originalName = getOriginalUserName();
            const pid = uatData.participant?.id?.toLowerCase() || '';

            // Add employee persona
            if (uatData.personas?.employee?.available) {
              const emp = uatData.personas.employee;
              // Use DB roles if returned by backend, fallback to base roles
              const empRoles = emp.roles?.length > 0
                ? emp.roles
                : ['employee'];
              allPersonas.push({
                id: `uat-emp-${emp.personnel_number}`,
                personnelNumber: emp.personnel_number,
                email: `test.${originalName.firstName?.toLowerCase()}.${pid}.employee@eduvos-uat.com`,
                fullName: emp.name,
                firstName: originalName.firstName || '',
                lastName: `${originalName.lastName} (Team Member)`,
                jobTitle: emp.job_title,
                department: emp.department,
                campus: emp.campus || 'UAT',
                campusName: emp.campus || 'UAT Campus',
                functionalArea: emp.functional_area,
                jobFamilyId: emp.functional_area?.toUpperCase().replace(/ /g, '_'),
                roles: empRoles,
                positionId: `POS-${emp.personnel_number}`,
                positionDescription: `${emp.job_title} - ${emp.department}`,
                managerPersonnelNumber: emp.reports_to,
                reportsToPositionId: emp.reports_to ? `POS-${emp.reports_to}` : undefined,
                isPrimary: true,
              });
            }

            // Add manager persona
            if (uatData.personas?.manager?.available) {
              const mgr = uatData.personas.manager;
              // Use DB roles if returned by backend, fallback to base roles
              const mgrRoles = mgr.roles?.length > 0
                ? mgr.roles
                : ['employee', 'manager'];
              allPersonas.push({
                id: `uat-mgr-${mgr.personnel_number}`,
                personnelNumber: mgr.personnel_number,
                email: `test.${originalName.firstName?.toLowerCase()}.${pid}.manager@eduvos-uat.com`,
                fullName: mgr.name,
                firstName: originalName.firstName || '',
                lastName: `${originalName.lastName} (People Leader)`,
                jobTitle: mgr.job_title,
                department: mgr.department,
                campus: mgr.campus || 'UAT',
                campusName: mgr.campus || 'UAT Campus',
                functionalArea: mgr.functional_area,
                jobFamilyId: mgr.functional_area?.toUpperCase().replace(/ /g, '_'),
                roles: mgrRoles,
                positionId: `POS-${mgr.personnel_number}`,
                positionDescription: `${mgr.job_title} - ${mgr.department}`,
                managerPersonnelNumber: mgr.reports_to,
                reportsToPositionId: mgr.reports_to ? `POS-${mgr.reports_to}` : undefined,
                isPrimary: true,
              });
            }

            // Add one-up manager persona (if available)
            if (uatData.personas?.one_up_manager?.available) {
              const oneUp = uatData.personas.one_up_manager;
              // Use DB roles if returned by backend, fallback to base roles
              const oneUpRoles = oneUp.roles?.length > 0
                ? oneUp.roles
                : ['employee', 'manager'];
              allPersonas.push({
                id: `uat-oneup-${oneUp.personnel_number}`,
                personnelNumber: oneUp.personnel_number,
                email: `test.${originalName.firstName?.toLowerCase()}.${pid}.oneup@eduvos-uat.com`,
                fullName: oneUp.name,
                firstName: originalName.firstName || '',
                lastName: `${originalName.lastName} (One-Up People Leader)`,
                jobTitle: oneUp.job_title,
                department: oneUp.department,
                campus: oneUp.campus || 'UAT',
                campusName: oneUp.campus || 'UAT Campus',
                functionalArea: oneUp.functional_area,
                jobFamilyId: oneUp.functional_area?.toUpperCase().replace(/ /g, '_'),
                roles: oneUpRoles,
                positionId: `POS-${oneUp.personnel_number}`,
                positionDescription: `${oneUp.job_title} - ${oneUp.department}`,
                managerPersonnelNumber: undefined,
                reportsToPositionId: undefined,
                isPrimary: true,
              });
            }
          }
        } catch (uatError) {
          console.warn('Could not load UAT personas:', uatError);
        }
      }
      
      // Also load general test personas
      const response = await fetch(`${apiBaseUrl}/integration/test-users/personas`, { headers: authHeaders });
      refused = refused || response.status === 403;
      const data = response.ok ? await response.json() : {};
      
      // Add general test personas (but mark them differently)
      if (data.personas) {
        allPersonas.push(...data.personas);
      }
      
      setPersonas(allPersonas);
      setNotEnabled(refused && allPersonas.length === 0);
      
      // Try to restore saved persona or use current user's employeeId
      const savedPersonaJson = localStorage.getItem('dev_selected_persona');
      if (savedPersonaJson) {
        try {
          const savedPersona = JSON.parse(savedPersonaJson);
          setSelectedPersonaId(savedPersona.id);
        } catch {
          // If parsing fails, use employeeId from current user
          const matchingPersona = allPersonas.find((p: TestPersona) => p.personnelNumber === user?.employeeId);
          if (matchingPersona) {
            setSelectedPersonaId(matchingPersona.id);
          }
        }
      } else {
        // No saved persona means user is using their real profile
        // Don't auto-select any test persona - keep selectedPersonaId empty
        setSelectedPersonaId('');
      }
    } catch (error) {
      console.error('Failed to load test personas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonaChange = (personaId: string) => {
    // Handle switching back to real profile
    if (personaId === 'real-profile') {
      setSelectedPersonaId('');
      clearTestPersona();
      // Close modal after switch
      setTimeout(() => onOpenChange(false), 300);
      return;
    }
    
    const persona = personas.find(p => p.id === personaId);
    if (persona) {
      setSelectedPersonaId(personaId);
      switchToTestPersona(persona);
      // Close modal after switch
      setTimeout(() => onOpenChange(false), 300);
    }
  };

  // Filter personas
  const filteredPersonas = (personas || []).filter(persona => {
    if (filterRole !== 'all' && !persona.roles.includes(filterRole)) {
      return false;
    }
    if (filterCampus !== 'all' && persona.campus !== filterCampus) {
      return false;
    }
    return true;
  });

  // Extract unique roles and campuses
  const uniqueRoles = Array.from(new Set(personas.flatMap(p => p.roles)));
  const uniqueCampuses = Array.from(new Set(personas.map(p => p.campus)));

  const selectedPersona = personas.find(p => p.id === selectedPersonaId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary-600" />
              <DialogTitle>Switch Test Persona</DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadPersonas}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
          <DialogDescription>
            Switch between your personalised UAT personas or general test users
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {notEnabled && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              Test personas are not enabled for your account. You can keep using your own profile.
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Filter by Role</label>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {uniqueRoles.map(role => (
                    <SelectItem key={role} value={role}>
                      {role.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Filter by Campus</label>
              <Select value={filterCampus} onValueChange={setFilterCampus}>
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="All campuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campuses</SelectItem>
                  {uniqueCampuses.map(campus => (
                    <SelectItem key={campus} value={campus}>
                      {campus}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Persona Selector */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              Select Test Persona ({filteredPersonas.length} available)
            </label>
            <Select value={selectedPersonaId || 'real-profile'} onValueChange={handlePersonaChange}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Using Real Profile" />
              </SelectTrigger>
              <SelectContent className="max-h-96">
                {/* Real Profile Option */}
                <SelectItem value="real-profile">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-2">
                        Use Real Profile
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          Your Account
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Switch back to your actual Azure AD profile
                      </div>
                    </div>
                  </div>
                </SelectItem>
                
                <div className="border-t my-1" />
                
                {/* UAT Personas Section */}
                {filteredPersonas.some(p => p.id.startsWith('uat-')) && (
                  <div className="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-50 border-b">
                    🎯 Your UAT Personas
                  </div>
                )}
                {filteredPersonas.filter(p => p.id.startsWith('uat-')).map(persona => {
                  const personaType = persona.id.includes('-emp-')
                    ? 'Team Member'
                    : persona.id.includes('-oneup-')
                      ? 'One-Up People Leader'
                      : 'People Leader';
                  const badgeColor = persona.id.includes('-oneup-')
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-amber-100 text-amber-700';
                  return (
                    <SelectItem key={persona.id} value={persona.id}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {persona.fullName}
                            <span className={`text-xs px-1.5 py-0.5 rounded ${badgeColor}`}>
                              {personaType}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {persona.jobTitle} • {persona.campus}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
                
                {/* General Test Personas Section */}
                {filteredPersonas.some(p => !p.id.startsWith('uat-')) && (
                  <div className="px-2 py-1 text-xs font-semibold text-gray-600 bg-gray-100 border-b border-t mt-1">
                    🧪 General Test Users
                  </div>
                )}
                {filteredPersonas.filter(p => !p.id.startsWith('uat-')).map(persona => (
                  <SelectItem key={persona.id} value={persona.id}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="font-medium">{persona.fullName}</div>
                        <div className="text-xs text-gray-500">
                          {persona.jobTitle} • {persona.campus}
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected Persona Details */}
          {selectedPersona && (
            <div className={`rounded-lg p-4 space-y-3 border ${
              selectedPersona.id.startsWith('uat-') 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-heritage-blue">
                      {selectedPersona.fullName}
                    </h4>
                    {selectedPersona.id.startsWith('uat-') && (
                      <Badge className={`text-white text-xs ${
                        selectedPersona.id.includes('-oneup-') ? 'bg-purple-500' : 'bg-amber-500'
                      }`}>
                        {selectedPersona.id.includes('-emp-')
                          ? 'UAT Team Member'
                          : selectedPersona.id.includes('-oneup-')
                            ? 'UAT One-Up People Leader'
                            : 'UAT People Leader'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{selectedPersona.email}</p>
                </div>
                <Badge variant="outline" className="bg-heritage-blue text-white">
                  {selectedPersona.personnelNumber}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Job Title</div>
                    <div className="font-medium">{selectedPersona.jobTitle}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Campus</div>
                    <div className="font-medium">{selectedPersona.campusName}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Function</div>
                    <div className="font-medium">{selectedPersona.functionalArea || selectedPersona.department}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Functional Area</div>
                    <div className="font-medium">{selectedPersona.functionalArea}</div>
                  </div>
                </div>
              </div>

              {/* Roles */}
              <div>
                <div className="text-xs text-gray-500 mb-2">Assigned Roles</div>
                <div className="flex flex-wrap gap-1">
                  {selectedPersona.roles.map(role => (
                    <Badge key={role} variant="secondary" className="text-xs">
                      {role.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Position Info */}
              <div className="pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Position Details</div>
                <div className="text-sm">
                  <div className="font-medium">{selectedPersona.positionDescription}</div>
                  <div className="text-xs text-gray-500">Position ID: {selectedPersona.positionId}</div>
                  {selectedPersona.reportsToPositionId && (
                    <div className="text-xs text-gray-500">
                      Reports to: {selectedPersona.reportsToPositionId}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
